import { ROUTE_PAYMENT, ROUTE_SIGN_IN } from '@/constants/routes';
import authService from '@/services/auth.service';
import envService from '@/services/env.service';
import { getRoute } from '@/utils';
import axios, { isAxiosError } from 'axios';
import qs from 'qs';
import apiClient from './api';

/**
 * Custom fetcher function for Orval-generated API client
 * Uses the apiClient with its interceptors for authentication
 */
export const customFetcher = <T>({
  url,
  method,
  params,
  data,
  headers,
  signal,
}: {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  params?: Record<string, any>;
  data?: any;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}): Promise<T> => {
  return apiClient({
    url,
    method,
    params,
    data,
    headers,
    signal,
  }).then((res) => res.data as T);
};

export const catchBackendApiClient = axios.create({
  baseURL: envService.getEnv().VITE_CATCH_BACKEND_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Use custom params serializer to handle arrays without brackets
  paramsSerializer: (params) => {
    return qs.stringify(params, { arrayFormat: 'repeat' });
  },
});

/**
 * Add a request interceptor to add the token to the headers
 */
catchBackendApiClient.interceptors.request.use(
  (config) => {
    // Send the authenticated user token if available
    const token = authService.getToken();
    if (token) {
      // Use standard Authorization Bearer header for member JWT
      config.headers['authorization'] = `Bearer ${token}`;
    } else {
      // Check for visitor/session token if no member token
      const visitorToken = authService.getVisitorToken();
      if (visitorToken) {
        // Use x-session-authorization header for session JWT
        config.headers['x-session-authorization'] = visitorToken;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Add a response interceptor to handle auth/membership errors consistently
 * with the legacy apiClient interceptor in api.ts.
 */
catchBackendApiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (!isAxiosError(error)) {
      return Promise.reject(error);
    }

    if (error?.response?.status === 401) {
      authService.logout();
      window.location.href = getRoute(ROUTE_SIGN_IN);
      return Promise.reject(error);
    } else if (error?.response?.status === 402 || error?.response?.status === 403) {
      authService.logout();
      window.location.href = getRoute(ROUTE_PAYMENT);
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export const customCatchBackendFetcher = <T>({
  url,
  method,
  params,
  data,
  headers,
  signal,
}: {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  params?: Record<string, any>;
  data?: any;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}): Promise<T> => {
  return catchBackendApiClient({
    url,
    method,
    params,
    data,
    headers,
    signal,
  }).then((res) => res.data as T);
};
